import { Box, Typography } from '@mui/material';
import {
  useNavigate,
  useCurrentUser,
  useCurrentAccount,
  useCurrentCompany,
} from 'hooks/api';
import { useCurrentHousehold } from 'hooks/api/useHousehold';
import React, { useEffect, useMemo, useState } from 'react';
import { Incorporation } from 'components/ProgressTracker/Incorporation';
import { Routes } from 'fnRoutes';
import { useMediaBreakpoints } from 'hooks';
import { AccountVerificationComponent } from 'components/ProgressTracker/AccountVerification/AccountVerificationComponent';
import { ReviewAndSignComponentBIB as ReviewAndSignComponent } from 'components/ProgressTracker/ReviewAndSign/ReviewAndSignComponentBIB';
import { DropboxSignTemplates } from 'services/dropboxSign';
import { Loading } from 'components/common';
import {
  isBusinessTaxOnly,
  isBusinessBusinessInABox,
} from 'components/ProgressTracker/util';
import { BusinessStatusLabel } from 'models/business';
import { ProgressTracker1120sOnly } from 'components/ProgressTracker/ProgressTracker1120sOnly';
import { ProgressTrackerBusinessInABox } from 'components/ProgressTracker/ProgressTrackerBusinessInABox';
import { useCurrentPaymentStatus } from 'hooks/api/useStripe';
import { IncorporationStatus } from 'components/IncorporationStatus/IncorporationStatus';
import { ENTITY_MAPPING } from 'constants/common';
import { useSignedDocument } from 'hooks/api/useDropboxSign';
import { ReviewAndPayComponent } from './ReviewAndPay/ReviewAndPayComponent';
import { BusinessPackageComponent } from './BusinessPackage/BusinessPackageComponent';
import { BankSetupBIB } from './BankSetup/BankSetupBIB';

export const ProgressTracker = () => {
  const { isMobile, isDesktop } = useMediaBreakpoints();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { currentAccount, isLoading } = useCurrentAccount();
  const { currentCompany: businessData } = useCurrentCompany();
  const { alreadyPaid, isLoading: isLoadingAlreadyPaid } =
    useCurrentPaymentStatus();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isSoleProp = businessData?.entityType === ENTITY_MAPPING.sole_prop;
  const incorporationFinished = useMemo(() => {
    if (!isSoleProp) {
      return true;
    }
    return businessData?.bibHubspot?.ticketStatus === 'COMPLETED';
  }, [businessData?.bibHubspot, isSoleProp]);

  const { currentHousehold } = useCurrentHousehold();
  const { signatureRequestTemplate } = businessData ?? {};

  const { signedDocuments } = useSignedDocument({
    businessId: businessData?.id,
    template: signatureRequestTemplate as DropboxSignTemplates,
  });

  const firstTwoSectionsCompleted = useMemo(
    () =>
      !(
        !currentHousehold?.head?.dob ||
        !businessData?.stateOfIncorporation ||
        !businessData?.businessAddress ||
        !businessData?.mailingAddress ||
        !currentHousehold?.homeAddress ||
        !currentUser?.bankName ||
        !currentUser?.routingNumber ||
        !currentUser?.bankAccountNumber ||
        !currentUser?.bankAccountType
      ),
    [currentHousehold, businessData, currentUser],
  );

  const showIncorporationStatus = useMemo(() => {
    if (incorporationFinished || !firstTwoSectionsCompleted) {
      return false;
    }
    return true;
  }, [incorporationFinished, firstTwoSectionsCompleted]);

  const reviewAndSignCompleted = useMemo(
    () => signedDocuments && signedDocuments.length > 0,
    [signedDocuments],
  );

  const numberOfAccountVerificationSteps = useMemo(() => {
    if (isSoleProp) {
      return 4;
    }
    if (isBusinessBusinessInABox(businessData)) {
      return 3;
    }
    return 5;
  }, [isSoleProp, businessData]);

  const secondSectionCompleted = useMemo(() => {
    const accountVerificationItems = businessData?.progress?.filter(
      (progressItem) => progressItem.group === 'accountVerification',
    );

    const completedSteps = accountVerificationItems?.reduce((count, item) => {
      const statusCompleted = item.status?.length > 0;

      return statusCompleted ? count + 1 : count;
    }, 0);

    return completedSteps === numberOfAccountVerificationSteps;
  }, [businessData?.progress]);

  const bankSetupCompleted = useMemo(
    () =>
      businessData?.progress?.some(
        (progressItem) =>
          progressItem.stage === 'bankInformation' &&
          progressItem.status.some(
            (statusItem) => statusItem.status === 'completed',
          ),
      ),
    [businessData?.progress],
  );

  const [expandedIncorporationSection, setExpandedIncorporationSection] =
    useState<string | false>(false);
  const [
    expandedAccountVerificationSection,
    setExpandedAccountVerificationSection,
  ] = useState<string | false>(false);
  const [expandedReviewAndPaySection, setExpandedReviewAndPaySection] =
    useState<string | false>(false);
  const [expandedReviewAndSignSection, setExpandedReviewAndSignSection] =
    useState<string | false>(false);
  const [expandedBankSetupSection, setExpandedBankSetupSection] = useState<
    string | false
  >(false);
  const [expandedBusinessPackageSection, setExpandedBusinessPackageSection] =
    useState<string | false>(false);

  const handleExpandIncorporationSection =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpandedIncorporationSection(isExpanded ? panel : false);
    };
  const handleExpandAccountVerificationSection =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpandedAccountVerificationSection(isExpanded ? panel : false);
    };
  const handleExpandReviewAndPaySection =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpandedReviewAndPaySection(isExpanded ? panel : false);
    };
  const handleExpandedReviewAndSignSection =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpandedReviewAndSignSection(isExpanded ? panel : false);
    };
  const handleExpandBankSetupSection =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpandedBankSetupSection(isExpanded ? panel : false);
    };
  const handleExpandedBusinessPackageSection =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpandedBusinessPackageSection(isExpanded ? panel : false);
    };

  const onCollapseAllSections = () => {
    setExpandedIncorporationSection(false);
    setExpandedAccountVerificationSection(false);
    setExpandedReviewAndPaySection(false);
    setExpandedReviewAndSignSection(false);
    setExpandedBankSetupSection(false);
    setExpandedBusinessPackageSection(false);
  };

  const onContinueToAccountVerification = () => {
    onCollapseAllSections();
    setExpandedAccountVerificationSection('account-verification');
  };

  const onContinueToReviewAndPay = () => {
    onCollapseAllSections();
    setExpandedReviewAndPaySection('review-and-pay');
  };

  const onContinueToBankSetup = () => {
    onCollapseAllSections();
    setExpandedBankSetupSection('bank-setup');
  };

  const onContinueToBusinessPackage = () => {
    onCollapseAllSections();
    setExpandedBusinessPackageSection('business-package');
  };

  const onContinueToReviewAndSign = () => {
    onCollapseAllSections();
    setExpandedReviewAndSignSection('review-and-sign');
  };

  const handleAccountVerificationOnContinue = () => {
    if (!alreadyPaid && firstTwoSectionsCompleted) {
      onContinueToReviewAndPay();
    }
    if (alreadyPaid && firstTwoSectionsCompleted) {
      onContinueToReviewAndSign();
    }
  };

  useEffect(() => {
    // if (currentUser && !companyData) {
    //   navigate(`${Routes.ENTITY_SELECTION}`);
    // }
    if (currentUser?.role === 'Admin') {
      // 'SuperAdmin' can still go to this page
      navigate(`${Routes.ADMIN_HOME}`);
    }
    if (businessData?.status === BusinessStatusLabel.ACTIVE) {
      navigate(`${Routes.CUSTOMER_HOME}`);
    }
  }, [currentUser, businessData, navigate]);

  if (isLoading || !currentAccount || isLoadingAlreadyPaid || !businessData) {
    return <Loading />;
  }

  if (isBusinessTaxOnly(businessData)) {
    return (
      <ProgressTracker1120sOnly
        business={businessData}
        account={currentAccount}
      />
    );
  }
  if (isBusinessBusinessInABox(businessData)) {
    return <ProgressTrackerBusinessInABox account={currentAccount} />;
  }

  return (
    <>
      <Typography variant="h4" align="center" sx={{ pt: 8 }} color="#0D2259">
        Let’s Get Your Business Incorporated
      </Typography>
      <Box
        sx={{
          width: 'auto',
          px: isMobile ? 2 : 9,
          py: 2,
          mt: 2,
        }}
      >
        <Incorporation
          isDesktop={isDesktop}
          handleExpand={handleExpandIncorporationSection}
          expanded={expandedIncorporationSection}
          onContinueToAccountVerification={onContinueToAccountVerification}
        />
        <AccountVerificationComponent
          handleExpand={handleExpandAccountVerificationSection}
          expanded={expandedAccountVerificationSection}
          onContinueToNextSection={handleAccountVerificationOnContinue}
        />
        {!alreadyPaid && (
          <ReviewAndPayComponent
            handleExpand={handleExpandReviewAndPaySection}
            expanded={expandedReviewAndPaySection}
            disabled={!firstTwoSectionsCompleted}
          />
        )}

        {showIncorporationStatus && alreadyPaid && (
          <IncorporationStatus
            business={businessData}
            account={currentAccount}
          />
        )}

        <ReviewAndSignComponent
          handleExpand={handleExpandedReviewAndSignSection}
          expanded={expandedReviewAndSignSection}
          onContinueToNextSection={onContinueToBankSetup}
          disabled={showIncorporationStatus || !secondSectionCompleted}
        />

        <BankSetupBIB
          isDesktop={isDesktop}
          handleExpand={handleExpandBankSetupSection}
          expanded={expandedBankSetupSection}
          onContinueToNextSection={onContinueToBusinessPackage}
          disabled={!reviewAndSignCompleted}
        />

        <BusinessPackageComponent
          handleExpand={handleExpandedBusinessPackageSection}
          expanded={expandedBusinessPackageSection}
          disabled={!bankSetupCompleted}
        />
      </Box>
    </>
  );
};
